import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Menu, X, ChevronLeft, ChevronRight, Coins, ChevronDown } from 'lucide-react';
import { useSettings } from '../../hooks/useSettings';
import { useAuth } from '../../contexts/AuthContext';
import useSubscription from '../../hooks/useSubscription';
import UserSidebar from './UserSidebar';
import BuyCreditsModal from './BuyCreditsModal';
import SupportWidget from './SupportWidget';

const UserLayout = () => {
  const { settings, loading: settingsLoading } = useSettings();
  const { user } = useAuth();
  const { subscription } = useSubscription();
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [systemHealth, setSystemHealth] = useState({ backend_ok: true, scraper_ok: false, message: null });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Collapsed sidebar is a per-viewer convenience, not a brand setting, so it
  // lives in localStorage rather than app_settings. Read lazily so the first
  // paint already has the right width.
  const [creditsOpen, setCreditsOpen] = useState(false);

  const [navCollapsed, setNavCollapsed] = useState(() => {
    try { return window.localStorage.getItem('app_nav_collapsed') === 'true'; }
    catch (e) { return false; }
  });

  useEffect(() => {
    try {
      if (navCollapsed) {
        document.documentElement.setAttribute('data-nav-collapsed', 'true');
      } else {
        document.documentElement.removeAttribute('data-nav-collapsed');
      }
      window.localStorage.setItem('app_nav_collapsed', String(navCollapsed));
    } catch (e) {
      // Storage blocked; the attribute still applies for this page load.
    }
  }, [navCollapsed]);

  const fetchSystemHealth = async () => {
    try {
      const resp = await fetch(`${API_BASE_URL}/api/health/system`);
      if (resp.ok) {
        const data = await resp.json();
        setSystemHealth({ backend_ok: !!data.backend_ok, scraper_ok: !!data.scraper_ok, message: data.message || null });
      }
    } catch (e) {}
  };

  useEffect(() => {
    fetchSystemHealth();
    const interval = setInterval(fetchSystemHealth, 60000);
    return () => clearInterval(interval);
  }, []);

  const isDownOverride = !!systemHealth.message && systemHealth.scraper_ok === false;
  const systemOnline = systemHealth.backend_ok && systemHealth.scraper_ok && !isDownOverride;

  if (settingsLoading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--gradient-brand)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 32, height: 32, border: '3px solid rgba(255, 255, 255, 0.3)', borderTop: '3px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--color-bg-app)' }}>
      {/* Brand accent bar across the top of the viewport */}
      <div className="app-accent-bar" />

      {/* Sidebar collapse toggle (desktop only; mobile uses the drawer) */}
      <button
        type="button"
        className="nav-collapse-toggle"
        onClick={() => setNavCollapsed((v) => !v)}
        aria-label={navCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        title={navCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {navCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>

      {/* Mobile Header */}
      <div className="mobile-header">
        <button
          onClick={() => setSidebarOpen(true)}
          style={{ background: 'none', border: 'none', color: 'white', padding: '8px', borderRadius: '6px', cursor: 'pointer' }}
        >
          <Menu size={20} />
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 12 }}>
          {settings?.product_logo && (
            <img src={settings.product_logo} alt="Logo" style={{ height: 24, objectFit: 'contain' }} />
          )}
          <span style={{ fontSize: 16, fontWeight: 700 }}>{settings?.product_name || 'Dashboard'}</span>
        </div>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="mobile-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar present on all pages */}
      <UserSidebar 
        appSettings={settings} 
        onBuyCredits={() => setShowBuyCredits(true)}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />

      {/* Credits pill + system status chip at top-right */}
      <div style={{ position: 'fixed', top: 12, right: 12, zIndex: 40, display: 'flex', alignItems: 'center', gap: 8 }}>
        {subscription && typeof subscription.credits_remaining === 'number' && (
          <div style={{ position: 'relative' }}>
            {/* Carries the figures the subscription card used to show, so
                dropping that card from the page header loses no information. */}
            <button
              type="button"
              onClick={() => setCreditsOpen((v) => !v)}
              aria-expanded={creditsOpen}
              title="Credit details"
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: 'var(--color-accent-soft)',
                color: 'var(--color-accent)',
                border: '1px solid var(--color-accent)',
                padding: '6px 12px', borderRadius: 999, cursor: 'pointer',
                fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap'
              }}
            >
              <Coins size={14} />
              Available Credits: {subscription.credits_remaining.toLocaleString()}
              <ChevronDown size={13} style={{ transform: creditsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }} />
            </button>

            {creditsOpen && (
              <>
                <div
                  onClick={() => setCreditsOpen(false)}
                  style={{ position: 'fixed', inset: 0, zIndex: 41 }}
                />
                <div style={{
                  position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 42,
                  minWidth: 240, padding: 14, borderRadius: 10,
                  background: 'var(--color-bg-surface)',
                  border: '1px solid var(--color-border)',
                  boxShadow: '0 10px 24px rgba(0,0,0,0.12)'
                }}>
                  {[
                    ['Credits remaining', subscription.credits_remaining, 'var(--color-accent)'],
                    ['Monthly allowance', subscription.credits_per_month, 'var(--color-text-primary)'],
                    ['Total credits used', subscription.total_credits_used, 'var(--color-text-primary)'],
                  ].map(([label, value, colour]) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 8, fontSize: 13 }}>
                      <span style={{ color: 'var(--color-text-muted)' }}>{label}</span>
                      <span style={{ fontWeight: 700, color: colour }}>{(value ?? 0).toLocaleString()}</span>
                    </div>
                  ))}
                  {subscription.plan_name && (
                    <div style={{ marginTop: 4, paddingTop: 10, borderTop: '1px solid var(--color-border)', fontSize: 12, color: 'var(--color-text-muted)' }}>
                      Plan: <strong style={{ color: 'var(--color-text-secondary)' }}>{subscription.plan_name}</strong>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => { setCreditsOpen(false); setShowBuyCredits(true); }}
                    style={{
                      marginTop: 12, width: '100%', padding: '8px 12px', borderRadius: 8,
                      border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700,
                      background: 'var(--gradient-brand)', color: 'var(--color-accent-contrast)'
                    }}
                  >
                    Buy Credits
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        <div title={systemOnline ? 'Services up' : 'Services down'} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '6px 10px', borderRadius: 999 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: systemOnline ? '#22c55e' : 'var(--color-danger)' }} />
          <span style={{ fontSize: 12, fontWeight: 600 }}>{systemOnline ? 'System Online' : (isDownOverride ? 'System Down' : 'System Issue')}</span>
        </div>
      </div>

      {/* Optional system down banner */}
      {!systemOnline && isDownOverride && systemHealth.message && (
        <div style={{ position: 'fixed', top: 52, right: 12, zIndex: 39, maxWidth: 480, background: 'var(--color-danger-bg-strong)', color: '#7f1d1d', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--color-danger-border)' }}>
          <strong>Notice:</strong> {systemHealth.message}
        </div>
      )}

      {/* Main content outlet */}
      <main className="sidebar-offset mobile-content" style={{ maxWidth: 1200, margin: '0 auto', padding: 24, marginLeft: 'var(--layout-content-offset)' }}>
        <Outlet />
      </main>

      {/* Buy Credits Modal */}
      <BuyCreditsModal isOpen={showBuyCredits} onClose={() => setShowBuyCredits(false)} currentUser={user || {}} />

      {/* Support Widget - Floating button available on all user pages */}
      <SupportWidget />

      <style jsx>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default UserLayout;